import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import type { AxiosError } from "axios";
import { CheckCircle2 } from "lucide-react";
import type { SubmitHandler } from "react-hook-form";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";

import type { ApiErrorResponse } from "@/api/api.types";
import { useSubmitAllForms } from "@/hooks/useSubmitAllForms";
import { useMultiStepFormStore } from "@/stores";
import type { ReviewInformationFormType } from "@/types";
import { reviewInformationSchema } from "@/types";
import {
  ControlledRadioGroupCard,
  FormField,
  NotQualifyModal,
  Textarea,
} from "@/ui";
import { YES_NO_OPTIONS, YES_NO_RADIO_OPTIONS } from "@/utils";
import { CardsSection } from "./CardsSection";
import FormButtons from "./FormButtons";

const ReviewInformationForm = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { setMultiStepFormData, multiStepFormData, clearMultiStepFormData } =
    useMultiStepFormStore();
  const {
    mutate: submitMultiStepForm,
    isPending: isSubmittingForm,
    isError,
  } = useSubmitAllForms();

  const {
    handleSubmit,
    control,
    register,
    reset,
    formState: { errors },
  } = useForm<ReviewInformationFormType>({
    defaultValues: { hasTextCommunication: YES_NO_OPTIONS.YES },
    resolver: zodResolver(reviewInformationSchema),
  });

  const onSubmit: SubmitHandler<ReviewInformationFormType> = (
    data: ReviewInformationFormType,
  ) => {
    setMultiStepFormData({ reviewInformationFormData: data });
    submitMultiStepForm(
      {
        ...multiStepFormData,
        reviewInformationFormData: data,
      },
      {
        onError: (error: unknown) => {
          const axiosError = error as AxiosError<ApiErrorResponse>;
          const code = axiosError.response?.data?.error?.code;

          if (code) {
            setIsModalOpen(true);
            return;
          }
          toast.error("Error submitting the form. Please try again");
        },
      },
    );
  };

  useEffect(() => {
    if (multiStepFormData?.reviewInformationFormData) {
      reset(multiStepFormData?.reviewInformationFormData);
    }
  }, [reset, multiStepFormData?.reviewInformationFormData]);

  return (
    <div className="flex flex-col gap-10">
      <div className="flex justify-between">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="text-primary-600" size={16} />
          <p className="font-extrabold">Review information before submitting</p>
        </div>
      </div>
      <CardsSection />
      <form
        className="grid grid-cols-2 gap-x-12 gap-y-10"
        onSubmit={handleSubmit(onSubmit)}
      >
        <FormField
          label={"Do you authorize us to communicate through text?"}
          error={errors.hasTextCommunication?.message}
        >
          <ControlledRadioGroupCard
            name="hasTextCommunication"
            control={control}
            options={YES_NO_RADIO_OPTIONS}
          />
        </FormField>
        <FormField
          label="Notes"
          error={errors.notes?.message}
          className="col-span-2"
        >
          <Textarea {...register("notes")} />
        </FormField>
        <FormButtons
          isSubmitting={isSubmittingForm}
          onSubmitOverride={
            isError
              ? () => {
                  setIsModalOpen(true);
                }
              : undefined
          }
        />
      </form>
      <NotQualifyModal
        show={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        handleEndIntake={() => clearMultiStepFormData()}
        title={"Brightree error"}
        description={
          "The patient was successfully created in our system, but there was an error when attempting to create the patient in Brightree."
        }
      />
    </div>
  );
};

export default ReviewInformationForm;
